import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  ButtonStyledIconLeft,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import {
  PlusIcon,
  SearchIcon,
  UsersIconWithBg,
} from "../../../components/styled/AllImages";
import ListTable from "../../../components/common/tables/ListTable";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import { UsersListColumns } from "../../../helpers/adminConstants/TableConstants";
import { UsersOverAllWrapper } from "./styled";
import UsersApi from "../../../services/api/usersApi";
import { useDebounce } from "../../../hooks/useDebounce";
import { toast } from "react-toastify";
import { getModuleOperations } from "../../../utils";

const Users = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [checkingUser, setCheckingUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [rowsData, setRowsData] = useState([]);

  const onDelete = (row) => {
    setUserId(row?.id);
    setShowDeleteModal(true);
  };

  const handleView = (user) => {
    navigate(`/users/view-user/${user?.id}`);
  };

  const handleEdit = (user) => {
    navigate(`/users/edit-user/${user?.id}`);
  };
  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query);
  };

  const getUsersList = async (page = 1, query) => {
    try {
      setLoading(true);
      const { data, code } = await UsersApi.usersList(page, query);
      if (code === 200) {
        const mappedUsers = data.all.data.map((user, index) => ({
          ...user,
          job_title: user?.user_detail?.job_title ?? "",
        }));
        setRowsData(mappedUsers);
        setTotalCount(data?.all?.total);
        setPerPage(data?.all?.per_page);
      } else {
        setRowsData([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      setCheckingUser(true);
      const response = await UsersApi.checkUserLimit();
      if (response?.data?.can_create) navigate("/users/create-user");
      else if (response?.message) toast.error(response?.message);
    } catch (error) {
    } finally {
      setCheckingUser(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await UsersApi.deleteUser(userId);
      if (response.code === 200) {
        toast.success(t("user_deleted_successfully"));
        setShowDeleteModal(false);
        getUsersList(activePage);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response?.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleStatusToggle = async (row) => {
    const newObj = {
      user_id: row.id,
      active: parseInt(row.active) === 1 ? 2 : 1,
    };
    try {
      const response = await UsersApi.changeUserStatus(newObj);
      if (response.code === 200) {
        toast.success(t("user_status_updated_successfully"));
        getUsersList(activePage);
      } else {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getUsersList(activePage, debouncedSearchQuery);
  }, [activePage, debouncedSearchQuery, language]);

  return (
    <UsersOverAllWrapper>
      <Box className="content-head">
        <Box className="w-fit d-flex align-items-center gap-3">
          <UsersIconWithBg />
          <SubHeadingStyled size="16px" weight="700" color="var(--text)">
            {t("users")}
          </SubHeadingStyled>
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          <FloatingInput
            name="search"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            icon={SearchIcon}
            onChange={handleSearch}
          />
          {getModuleOperations(1, 1) && (
            <ButtonStyledIconLeft
              label={t("create_user")}
              icon={PlusIcon}
              isLoading={checkingUser}
              disabled={checkingUser}
              onClick={handleAddUser}
            />
          )}
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            typeId={1}
            options={["view", "edit", "delete", "status"]}
            rows={rowsData}
            columns={UsersListColumns}
            onDelete={onDelete}
            onOpen={handleView}
            onEdit={handleEdit}
            onStatusToggle={handleStatusToggle}
            activePage={activePage}
            setActivePage={setActivePage}
            totalCount={totalCount}
            perPage={perPage}
          />
        )}
      </Box>
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        title={t("user")}
        isLoading={loadingDelete}
      />
    </UsersOverAllWrapper>
  );
};

export default Users;
